// Spec: .github/specs/maintenance-mode-fe.md
// Polls the maintenance status endpoint and drives navigation to/from the maintenance page.

import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApiService } from '../api-service/api-service';
import { endPoints } from '../../constants/endpoints';
import { ROUTER_CONSTANTS } from '../../constants/router.constants';
import { MonitoringService } from '../app-monitoring-service/app-monitoring.service';
import {
  DEFAULT_AUTO_REFRESH_INTERVAL_SECONDS,
  DEFAULT_CONTACT_SUPPORT_EMAIL,
  DEFAULT_RESTORATION_MESSAGE,
  MaintenanceStatus,
  MaintenanceStatusApiResponse,
  mapMaintenanceStatus,
} from '../../models/maintenance-status.model';

const SYSTEM_NAME = 'DcpPortal';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService implements OnDestroy {
  private readonly statusSubject =
    new BehaviorSubject<MaintenanceStatus | null>(null);
  readonly status$ = this.statusSubject.asObservable();

  private isPolling = false;
  private pollTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private isMaintenanceActive = false;
  private hasConfirmedEnabledTrue = false;
  private hasLoggedCurrentActivation = false;
  private lastUrl = '';

  constructor(
    private readonly apiService: ApiService,
    private readonly router: Router,
    private readonly monitoringService: MonitoringService,
  ) {}

  /**
   * Starts self-scheduling polling of the maintenance status endpoint (AC-3).
   * Dormant until triggered by activateImmediately(); stops again once maintenance resolves.
   */
  startPolling(): void {
    if (this.isPolling) {
      return;
    }
    this.isPolling = true;
    this.poll();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  stopPolling(): void {
    this.isPolling = false;
    if (this.pollTimeoutId) {
      clearTimeout(this.pollTimeoutId);
      this.pollTimeoutId = undefined;
    }
  }

  /**
   * Called by the RequestInterceptor when a live API call signals maintenance (e.g. 503),
   * so the user is redirected immediately instead of waiting for the next poll (AC-4).
   * This is also what starts the recurring status poll, since polling is otherwise dormant.
   */
  activateImmediately(): void {
    const current = this.statusSubject.value;
    this.applyStatus(
      current?.enabled
        ? current
        : {
            enabled: true,
            restorationMessage:
              current?.restorationMessage ?? DEFAULT_RESTORATION_MESSAGE,
            contactSupportEmail:
              current?.contactSupportEmail ?? DEFAULT_CONTACT_SUPPORT_EMAIL,
            autoRefreshIntervalSeconds:
              current?.autoRefreshIntervalSeconds ??
              DEFAULT_AUTO_REFRESH_INTERVAL_SECONDS,
          },
      false, // locally-guessed, not yet confirmed by the real status endpoint
    );
    this.startPolling();
  }

  /**
   * Logs a single encounter of the maintenance page per activation, not per poll (AC-5).
   */
  logEncounterOnce(): void {
    if (this.hasLoggedCurrentActivation) {
      return;
    }
    this.hasLoggedCurrentActivation = true;
    this.apiService
      .callPostApi(endPoints.casemgmt.maintenance.log.URI, {
        system: SYSTEM_NAME,
      })
      .pipe(catchError((error) => this.logAndSwallow(error)))
      .subscribe();
  }

  private poll(): void {
    this.apiService
      .callGetApi<MaintenanceStatusApiResponse>(
        `${endPoints.casemgmt.maintenance.status.URI}/${SYSTEM_NAME}`,
      )
      .pipe(catchError((error) => this.logAndSwallow(error)))
      .subscribe((response) => {
        // AC-6: an unreachable status API must never trigger the maintenance page
        if (response) {
          this.applyStatus(mapMaintenanceStatus(response), true);
        }
        this.scheduleNextPoll();
      });
  }

  private scheduleNextPoll(): void {
    if (!this.isPolling) {
      return;
    }
    const intervalSeconds =
      this.statusSubject.value?.autoRefreshIntervalSeconds ??
      DEFAULT_AUTO_REFRESH_INTERVAL_SECONDS;
    this.pollTimeoutId = setTimeout(() => this.poll(), intervalSeconds * 1000);
  }

  private applyStatus(
    status: MaintenanceStatus,
    isRealConfirmation: boolean,
  ): void {
    this.statusSubject.next(status);

    if (isRealConfirmation && status.enabled) {
      this.hasConfirmedEnabledTrue = true;
    }

    if (status.enabled && !this.isMaintenanceActive) {
      this.isMaintenanceActive = true;
      if (!this.router.url.includes(ROUTER_CONSTANTS.maintenance)) {
        this.lastUrl = this.router.url;
      }
      this.router.navigate([ROUTER_CONSTANTS.maintenance]);
    } else if (
      !status.enabled &&
      this.isMaintenanceActive &&
      this.hasConfirmedEnabledTrue
    ) {
      // only a real confirmed true->false swing counts as resolution;
      // a false right after the 503-triggered guess is ignored so polling keeps going
      this.isMaintenanceActive = false;
      this.hasConfirmedEnabledTrue = false;
      this.hasLoggedCurrentActivation = false;
      this.router.navigateByUrl(this.lastUrl || ROUTER_CONSTANTS.dashboard);
      this.stopPolling();
    }
  }

  private logAndSwallow(error: any) {
    this.monitoringService.logException(
      new Error(error?.message ?? 'Maintenance service request failed'),
    );
    return of(null);
  }
}
